import React from "react";

import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import GridOnOutlinedIcon from "@mui/icons-material/GridOnOutlined";

import { getServiceUri } from "@auto/metadata";
import { t } from "@auto/services";

const filenameFromDisposition = (disposition, fallbackFilename) => {
  const match = String(disposition || "").match(
    /filename\*?=(?:UTF-8''|")?([^";]+)/i
  );

  if (!match?.[1]) {
    return fallbackFilename;
  }

  try {
    return decodeURIComponent(match[1].trim());
  } catch {
    return match[1].trim();
  }
};

/** Uses the same Excel download control for each operational report. */
export default function ReportExcelExportButton({
  endpoint,
  fallbackFilename,
  getPayload,
  errorMessageKey = "reporting.exportFailed"
}) {
  const [exporting, setExporting] = React.useState(false);

  const handleExport = async () => {
    if (exporting) {
      return;
    }

    setExporting(true);
    try {
      const response = await fetch(getServiceUri() + endpoint, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(getPayload())
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const blob = await response.blob();
      const filename = filenameFromDisposition(
        response.headers.get("Content-Disposition"),
        fallbackFilename
      );
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error(`Excel export failed: ${endpoint}`, error);
      window.alert(t(errorMessageKey));
    } finally {
      setExporting(false);
    }
  };

  return (
    <Tooltip title={t(exporting ? "reporting.exportingExcel" : "reporting.exportExcel")}>
      <span>
        <IconButton
          size="small"
          disabled={exporting}
          aria-label={t("reporting.exportExcel")}
          onClick={handleExport}
        >
          {exporting
            ? <CircularProgress size={18} />
            : <GridOnOutlinedIcon fontSize="small" />}
        </IconButton>
      </span>
    </Tooltip>
  );
}
